import { useCallback, useEffect, useMemo } from "react"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { useCountryCodeToLicensePlate, useUser, useWorkTask } from "@tm/context-distribution"
import { VinFieldProps } from "@tm/controls"
import { useLocalization } from "@tm/localization"
import { ESubModules, UserModuleType } from "@tm/models"
import { useActions } from "@tm/morpheus"
import { getCostPerCalculation, initSelector } from "@tm/utils"
import GeneralVehicleForm from "@bundles/damage-calculation-dat/components/_shared/GeneralVehicleFields"
import { useGetVins } from "@tm/data/VinPicker/useGetVins"
import { getBundleParams } from "../../../utils"
import { MainActions, fieldsSelector, vehicleSelector } from "../../main/business"
import { getGlassVehicleSchema } from "../business/helpers"

export const vehicleFieldsSelector = createSelector(fieldsSelector, (fields) => {
    const { registrationNo, initialRegistration, mileage, plateNumber, vin, registrationNos, minDate, processName } = fields
    return { registrationNo, initialRegistration, mileage, plateNumber, vin, registrationNos, minDate, processName }
})

const tecdocselector = createSelector(vehicleSelector, (vehicle) => vehicle?.tecDocTypeId)

const selector = initSelector(
    vehicleFieldsSelector,
    tecdocselector,
    (s) => s.summary.vinSearchInformalMessage,
    (s) => s.summary.datVinSearchIsValid,
    (s) => s.summary.checkVinLoading,
    (s) => s.summary.showVehicleModelDetailsLoading,
    (s) => s.summary.isGermanOrAustrianVehicle
)

export default function VehicleInfoFields() {
    const { enableDATVinSearch, useAttachToWorktask } = getBundleParams()
    const localization = useLocalization()
    const user = useUser()
    const { translateText } = localization
    const { workTask, attachToWorkTask } = useWorkTask() ?? {}
    const vehicle = workTask?.vehicle
    const actions = useActions(MainActions, "updateFieldValue", "validateVin", "loadVehicleDetails", "initVehicle", "setValidateFields")
    const infoSelector = useSelector(selector)
    const [
        fields,
        tecDoc,
        vinSearchInformalMessage,
        datVinSearchIsValid,
        checkVinLoading,
        showVehicleModelDetailsLoading,
        isGermanOrAustrianVehicle,
    ] = infoSelector
    const vehicleVinSuggestions: string[] = useGetVins("", tecDoc) ?? []
    const glassSchema = getGlassVehicleSchema(translateText, vehicle, isGermanOrAustrianVehicle)

    const { countryCode } = useCountryCodeToLicensePlate(vehicle?.countryCode)

    useEffect(() => {
        actions?.initVehicle && actions.initVehicle(countryCode)
    }, [vehicle?.countryCode])

    const handleGetVinSuggestions = useCallback(
        (query: string): Array<string> => {
            return vehicleVinSuggestions?.length && query?.length
                ? vehicleVinSuggestions?.filter((vin) => vin.startsWith(query))
                : vehicleVinSuggestions
        },
        [vehicle, fields.vin, vehicleVinSuggestions]
    )

    const handleValidateVin = useCallback(() => {
        actions.validateVin(attachToWorkTask, translateText)
    }, [actions, attachToWorkTask, translateText])

    const onHandleUpdateValue = useCallback(
        (path: string, value: string | Date | number) => {
            actions?.updateFieldValue(path, value)
        },
        [actions]
    )

    const handleFormValid = useCallback(
        (isValid: boolean) => {
            actions.setValidateFields("vehicleValid", isValid)
        },
        [actions]
    )

    const calculationCost = useMemo(() => {
        return getCostPerCalculation(UserModuleType.TruckGarage, ESubModules.DamageCalculationGlass, user?.userContext)
    }, [user?.userContext])

    const vinFieldProps: VinFieldProps = useMemo(() => {
        const base: VinFieldProps = {
            getSuggestionData: handleGetVinSuggestions,
        }

        if (useAttachToWorktask && enableDATVinSearch) {
            return {
                ...base,
                datVinSearchIsValid,
                vinSearchRequired: true,
                vinSearchInformalMessage,
                datPricePerRequest: calculationCost?.toString(),
                checkVinLoading,
                translateText,
                validateVin: handleValidateVin,
            }
        }

        return base
    }, [
        handleGetVinSuggestions,
        useAttachToWorktask,
        enableDATVinSearch,
        datVinSearchIsValid,
        vinSearchInformalMessage,
        calculationCost,
        checkVinLoading,
        translateText,
        handleValidateVin,
    ])

    return (
        <GeneralVehicleForm
            onFormValid={handleFormValid}
            fields={fields}
            onUpdateFieldValue={onHandleUpdateValue}
            vehicle={vehicle}
            schema={glassSchema}
            vinFieldProps={vinFieldProps}
            isGermanOrAustrianVehicle={isGermanOrAustrianVehicle}
            showVehicleModelDetailsLoading={showVehicleModelDetailsLoading}
        />
    )
}
